import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Assignment, WorkPlanSummary } from '@apaq/orgkit-js';
import { DateEvent } from '../../../../../common/scheduler/date-event';
import log from 'loglevel';
import { DataSource } from 'src/app/common/scheduler/datasource';
import { ContextService } from 'ngx-orgkit';

const LOCAL_STORAGE_RESOURCES = 'plan-timeline.selected-resources';

export interface DropData {
  resourceId: string,
  date: Date;
  assignmentIds: string[];
}

@Component({
  selector: 'ws-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss']
})
export class TimelineComponent implements AfterViewInit {

  @ViewChild('timeline') timeline?: ElementRef;

  @Output() eventSelected = new EventEmitter<DateEvent>();
  @Output() drop = new EventEmitter();
  @Output() dateClick = new EventEmitter<any>();
  _dataSource?: DataSource;

  constructor(private context: ContextService) {
    this.onResourceSelected = this.onResourceSelected.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.onMove = this.onMove.bind(this);
  }

  async ngAfterViewInit() {
    const storageId = await this.storageId();

    if (localStorage.getItem(storageId) != null) {
      this.timeline!.nativeElement.selectedResources = JSON.parse(localStorage.getItem(storageId!)!);
    }

    this.timeline!.nativeElement.addEventListener('slResourceSelect', this.onResourceSelected);
    this.timeline!.nativeElement.addEventListener('slSelect', this.onSelect);
    this.timeline!.nativeElement.addEventListener('click', this.onClick);
    this.timeline!.nativeElement.addEventListener('drop', this.onDrop);
    this.timeline!.nativeElement.addEventListener('slMove', this.onMove);

    if (this.dataSource != null) {
      this.timeline!.nativeElement.dataSource = this.dataSource;
    }
  }

  ngOnDestroy() {
    this.timeline!.nativeElement.removeEventListener('slResourceSelect', this.onResourceSelected);
    this.timeline!.nativeElement.removeEventListener('slSelect', this.onSelect);
    this.timeline!.nativeElement.removeEventListener('click', this.onClick);
    this.timeline!.nativeElement.removeEventListener('drop', this.onDrop);
    this.timeline!.nativeElement.removeEventListener('slMove', this.onMove);
  }



  get dataSource() {
    return this._dataSource!;
  }

  @Input()
  set dataSource(value: DataSource) {
    this._dataSource = value;
    if (this.timeline != null) {
      this.timeline!.nativeElement.dataSource = this._dataSource;
    }
  }

  select(id: string) {
    this.timeline?.nativeElement.select(id);
  }

  private async storageId() {
    const orgId = this.context.organizationId;
    return orgId + '_' + LOCAL_STORAGE_RESOURCES;
  }

  private async onResourceSelected(ev: CustomEvent) {
    const selectedResources = ev.detail;
    localStorage.setItem(await this.storageId(), JSON.stringify(selectedResources));
  }

  private async onSelect(ev: CustomEvent) {
    this.eventSelected.emit(ev.detail);
  }

  private onClick(ev: MouseEvent) {
    this.timeline!.nativeElement.getEventProperties(ev).then((props: any) => {
      if (props == null) return;
      if (props.dateEvents.length === 0) {
        this.dateClick.emit(props);
      }
    });
  }

  private onDrop(ev: DragEvent) {
    log.debug('Drop on timeline: ', ev);
    this.timeline!.nativeElement.getEventProperties(ev).then((props: any) => {
      let mimetype = 'application/assignment-list-json';
      if (ev.dataTransfer!.getData(mimetype) !== '') {
        const assignments: Assignment[] = JSON.parse(ev.dataTransfer!.getData(mimetype));
        const date: Date = props.date;
        const resourceId = props.resourceId;
        const assignmentIds = assignments.map(a => a.id);

        this.drop.emit({ date, resourceId, assignmentIds });
      }

    });

    ev.preventDefault();
    ev.stopPropagation();
  }

  private onMove(ev: CustomEvent) {
    log.debug('Date event moved: ', ev);
    let now = new Date();
    const props = ev.detail;
    let summary: WorkPlanSummary = props.dateEvent.summary;

    // do move
    let ids: string[] = [];
    let lockCount = 0;
    let notifiedCount = 0;
    summary.assignments.forEach((a) => {
      if (a.action === 'None') {
        ids.push(a.assignmentId);

        if (a.locked) {
          lockCount++;
        }

        if (a.notifiedOfImpendingWork) {
          notifiedCount++;
        }
      }

    })

    let dropData: DropData = {
      resourceId: props.resourceId,
      date: props.date,
      assignmentIds: ids
    };

    this.drop.emit(dropData);
  }

}